import axios from 'axios';
import { useEffect, useState } from 'react';
import API_URL from '../config/api';
import './Dashboard.css';
import './Profile.css';

const Profile = ({ user, setUser }) => {
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'password'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    dateOfBirth: '',
    country: '',
    currency: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Always fetch fresh profile data on mount so dateOfBirth is always current
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API_URL}/users/profile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const freshUser = response.data;
        setFormData({
          name: freshUser.name || '',
          email: freshUser.email || '',
          dateOfBirth: freshUser.dateOfBirth ? new Date(freshUser.dateOfBirth).toISOString().split('T')[0] : '',
          country: freshUser.country || '',
          currency: freshUser.currency || ''
        });
      } catch (err) {
    // Fallback to prop if fetch fails
        if (user) {
          setFormData({
            name: user.name || '',
            email: user.email || '',
            dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '',
            country: user.country || '',
            currency: user.currency || ''
          });
        }
      }
    };
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/users/profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh profile to get updated dateOfBirth and sync App state
      const refreshed = await axios.get(
        `${API_URL}/users/profile`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const freshUser = refreshed.data;
      setFormData({
        name: freshUser.name || '',
        email: freshUser.email || '',
        dateOfBirth: freshUser.dateOfBirth ? new Date(freshUser.dateOfBirth).toISOString().split('T')[0] : '',
        country: freshUser.country || '',
        currency: freshUser.currency || ''
      });
      localStorage.setItem('user', JSON.stringify(freshUser));
      if (setUser) setUser(freshUser);
      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }

    setPasswordLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/users/change-password`,
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPasswordSuccess('Password changed successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>User Profile</h1>
        <p>Manage your personal information and security settings</p>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        <button
          className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile Information
        </button>
      </div>

      {/* Profile Information Tab */}
      {activeTab === 'profile' && (
        <>
          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="profile-section">
            <form onSubmit={handleSubmit} className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    className="form-input"
                    disabled
                    title="Email cannot be changed"
                  />
                  <small className="form-text">Email address cannot be changed</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group half-width">
                  <label htmlFor="country" className="form-label">Country</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    className="form-input"
                    disabled
                    title="Country cannot be changed"
                  />
                </div>

                <div className="form-group half-width">
                  <label htmlFor="currency" className="form-label">Currency</label>
                  <input
                    type="text"
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    className="form-input"
                    disabled
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateOfBirth" className="form-label">Date of Birth</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default Profile;

